import { useEffect, useId, useRef, useState } from "react";
import { formatRegionSelection } from "@/app/selectors";
import { useApp, useUi, useUiDispatch } from "@/app/AppContext";
import { useI18n } from "@/i18n/useI18n";

/** Multi-select dropdown for the location filter (empty selection = all). */
export function RegionSelect() {
  const { t } = useI18n();
  const { data } = useApp();
  const { selectedRegions } = useUi();
  const dispatch = useUiDispatch();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const idPrefix = useId();

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const options = [...new Set([...data.dataset.regions, ...selectedRegions])]
    .filter(Boolean)
    .sort();
  const toggle = (region: string) => {
    const next = new Set(selectedRegions);
    if (!next.delete(region)) next.add(region);
    dispatch({ type: "setRegions", regions: next });
  };

  return (
    <div id="location-selector-wrapper">
      <label>
        <span id="label-location">{t("location")}</span>
        <span className="toolbar-label-colon">:</span>
      </label>
      <div className="custom-select" id="region-select-wrapper" ref={wrapperRef}>
        <button
          type="button"
          className="custom-select-trigger"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {formatRegionSelection(selectedRegions, t("allLocations"), t("locationsSelected"))}
        </button>
        <div className={`custom-select-options${open ? " open" : ""}`}>
          {options.map((region, index) => {
            const id = `${idPrefix}-${index}`;
            return (
              <div key={region} className="custom-select-option">
                <input
                  type="checkbox"
                  id={id}
                  checked={selectedRegions.has(region)}
                  onChange={() => toggle(region)}
                />
                <label htmlFor={id} style={{ cursor: "pointer", userSelect: "none" }}>
                  {region}
                </label>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
